/**
 * AdminExtensions — the approval queue for student-requested 30-day
 * campaign extensions.
 *
 * Panel structure:
 *   1. Pending queue    — each row shows the student's reason, the
 *                         campaign context, and Approve / Deny controls
 *                         with an audit-note textarea.
 *   2. Recent decisions — audit trail of already-approved and denied
 *                         requests so ops can spot patterns.
 *
 * The extension length (in days) is read from the live platform_policy
 * config, so if the board later moves from 30 → 45 days, this queue
 * picks it up on next render.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, Check, X, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { SectionHeader, AdminButton, EmptyState, StatusBadge } from "./_ui";
import {
  getPendingExtensions,
  getAllExtensions,
  approveExtension,
  denyExtension,
  subscribeExtensions,
} from "../../lib/campaignExtensions";
import { getPolicy, subscribePolicy } from "../../lib/platformPolicy";
import { CAMPAIGN_ROSTER } from "../../data/adminMock";

const relTime = (iso) => {
  if (!iso) return "";
  const secs = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60)             return `${secs}s ago`;
  if (secs < 3600)           return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400)          return `${Math.round(secs / 3600)}h ago`;
  if (secs < 86400 * 30)     return `${Math.round(secs / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

export const AdminExtensions = () => {
  const [pending, setPending] = useState(() => getPendingExtensions());
  const [all, setAll] = useState(() => getAllExtensions());
  const [policy, setPolicy] = useState(getPolicy);
  const [note, setNote] = useState({});

  useEffect(() => subscribePolicy(setPolicy), []);
  useEffect(
    () =>
      subscribeExtensions(() => {
        setPending(getPendingExtensions());
        setAll(getAllExtensions());
      }),
    [],
  );

  const onApprove = (id) => {
    approveExtension(id, note[id] || "");
    toast.success("Extension approved", {
      description: `${policy.extensionDays} days added to the campaign. Student is notified.`,
    });
    setNote((prev) => ({ ...prev, [id]: "" }));
  };

  const onDeny = (id) => {
    denyExtension(id, note[id] || "");
    toast.message("Extension denied", {
      description: "Decision is logged. The student sees the outcome on their campaign page.",
    });
    setNote((prev) => ({ ...prev, [id]: "" }));
  };

  const decisions = all.filter((e) => e.status !== "pending").slice(0, 8);

  return (
    <div className="space-y-8">
      <SectionHeader
        eyebrow="Extension requests"
        title="Approve or deny student-requested extensions."
        description={`Board policy allows one ${policy.extensionDays}-day extension per campaign, with admin sign-off. Each decision is logged with your name, timestamp, and note.`}
      />

      {pending.length === 0 ? (
        <EmptyState
          title="No extension requests pending"
          description="When a student requests an extension, the case will appear here."
        />
      ) : (
        <ul className="space-y-5" data-testid="extensions-pending-list">
          {pending.map((e) => {
            const campaign = CAMPAIGN_ROSTER[e.campaignId];
            return (
              <li
                key={e.id}
                data-testid={`extension-row-${e.id}`}
                className="overflow-hidden rounded-lg border border-cobalt/25 bg-cobalt/[0.04]"
              >
                <div className="flex items-center justify-between gap-3 border-b border-cobalt/20 bg-cobalt/[0.06] px-5 py-3">
                  <div className="flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5 text-cobalt" strokeWidth={2} />
                    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-cobalt">
                      Extension requested
                    </span>
                    <span className="text-[11px] text-ink/60">
                      · {relTime(e.requestedAt)}
                    </span>
                  </div>
                  {campaign && (
                    <span className="text-[11px] italic text-ink/60">{campaign.name}</span>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-5 px-5 py-5 lg:grid-cols-12">
                  {/* Left: request context */}
                  <div className="lg:col-span-7">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-ink">{e.requesterName || "Student"}</span>
                      {campaign && (
                        <>
                          <span className="text-ink/40">·</span>
                          <span className="text-ink/70">{campaign.school}</span>
                        </>
                      )}
                    </div>

                    <p className="mt-3 rounded-md border border-ink/10 bg-beige/60 p-4 text-sm leading-relaxed text-ink">
                      {e.reason || <em className="text-ink/40">No reason provided.</em>}
                    </p>

                    {campaign && (
                      <Link
                        to={`/demo/campaign/${e.campaignId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-ink hover:text-cobalt"
                        data-testid={`extension-view-${e.id}`}
                      >
                        View campaign
                        <ExternalLink className="h-3 w-3" strokeWidth={2} />
                      </Link>
                    )}
                  </div>

                  {/* Right: decision */}
                  <div className="lg:col-span-5">
                    <label className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">
                      Decision note (optional)
                    </label>
                    <textarea
                      rows={3}
                      value={note[e.id] || ""}
                      onChange={(ev) => setNote((prev) => ({ ...prev, [e.id]: ev.target.value }))}
                      placeholder="Context for the audit trail..."
                      className="mt-2 w-full rounded-md border border-ink/15 bg-sand p-3 text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-ink/20"
                      data-testid={`extension-note-${e.id}`}
                    />

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <AdminButton
                        variant="approve"
                        size="sm"
                        onClick={() => onApprove(e.id)}
                        testid={`extension-approve-${e.id}`}
                      >
                        <Check className="h-3 w-3" strokeWidth={2} /> Approve · +{policy.extensionDays} days
                      </AdminButton>
                      <AdminButton
                        variant="reject"
                        size="sm"
                        onClick={() => onDeny(e.id)}
                        testid={`extension-deny-${e.id}`}
                      >
                        <X className="h-3 w-3" strokeWidth={2} /> Deny
                      </AdminButton>
                    </div>
                    <p className="mt-2 text-[11px] italic text-ink/50">
                      Approvals add {policy.extensionDays} days to the campaign end date and mark the extension as used.
                    </p>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Recent decisions */}
      {decisions.length > 0 && (
        <div>
          <h4 className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">
            Recent decisions
          </h4>
          <ul
            data-testid="extensions-decisions"
            className="mt-3 divide-y divide-ink/10 overflow-hidden rounded-lg border border-ink/10 bg-sand"
          >
            {decisions.map((e) => {
              const campaign = CAMPAIGN_ROSTER[e.campaignId];
              return (
                <li
                  key={e.id}
                  className="flex items-center gap-4 px-5 py-3"
                  data-testid={`extension-history-${e.id}`}
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cobalt/12">
                    <Clock className="h-3.5 w-3.5 text-cobalt" strokeWidth={2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-ink">
                      <span className="font-medium">{e.requesterName}</span>
                      <span className="text-ink/60">
                        {" "}· {campaign ? campaign.name : "campaign"} · {relTime(e.decidedAt || e.requestedAt)}
                      </span>
                    </p>
                    {e.moderatorNote && (
                      <p className="mt-0.5 truncate text-xs italic text-ink/60">
                        {e.moderatorNote}
                      </p>
                    )}
                  </div>
                  <StatusBadge status={e.status === "approved" ? "live" : "closed"} />
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
